/**
 * Admin Feature Flags Page
 *
 * Enable/disable features across the site.
 */

import { useState, useEffect } from 'react';
import { useAdminApi } from '../../hooks/useAdminApi';
import type { FeatureFlags } from '@petmas/shared';
import { UI_CONSTANTS } from '@petmas/shared';
import { asFeatureFlags } from '../../utils/typeAssertions';

// Only boolean feature flags (excludes googleClientId which is string | null)
type BooleanFeatureKey = Exclude<keyof FeatureFlags, 'googleClientId'>;

interface FeatureConfig {
  key: BooleanFeatureKey;
  label: string;
  description: string;
  category: 'core' | 'ai' | 'marketing';
}

const featureConfigs: FeatureConfig[] = [
  {
    key: 'aiChatbotEnabled',
    label: 'Chatbot IA',
    description: 'Muestra el chatbot de asistencia con IA en todas las paginas',
    category: 'ai',
  },
  {
    key: 'stockByLocationEnabled',
    label: 'Stock por Ubicacion',
    description: 'Muestra el stock disponible por cada tienda fisica',
    category: 'core',
  },
  {
    key: 'notifyMeEnabled',
    label: 'Notificame',
    description: 'Permite a los usuarios suscribirse para recibir notificaciones cuando un producto vuelva a estar disponible',
    category: 'marketing',
  },
  {
    key: 'blogEnabled',
    label: 'Blog',
    description: 'Habilita la seccion de blog con articulos sobre cuidado de mascotas',
    category: 'marketing',
  },
  {
    key: 'googleSigninEnabled',
    label: 'Inicio con Google',
    description: 'Permite a los usuarios iniciar sesion con su cuenta de Google',
    category: 'core',
  },
  {
    key: 'newsletterEnabled',
    label: 'Newsletter',
    description: 'Muestra el formulario de suscripcion al boletin en la pagina principal',
    category: 'marketing',
  },
];

const defaultSettings: FeatureFlags = {
  aiChatbotEnabled: true,
  stockByLocationEnabled: false,
  notifyMeEnabled: false,
  blogEnabled: false,
  googleSigninEnabled: false,
  googleClientId: null,
  newsletterEnabled: true,
};

export function AdminFeaturesPage() {
  const { getSettings, updateSettings, isLoading, error: apiError } = useAdminApi();
  const [settings, setSettings] = useState<FeatureFlags>(defaultSettings);
  const [isSaving, setIsSaving] = useState(false);
  const [saveSuccess, setSaveSuccess] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    loadSettings();
  }, []);

  const loadSettings = async () => {
    try {
      const data = await getSettings('features');
      if (data) {
        setSettings({ ...defaultSettings, ...asFeatureFlags(data) });
      }
    } catch {
      setError('Error al cargar configuracion');
    }
  };

  const handleToggle = async (key: BooleanFeatureKey) => {
    const newSettings = { ...settings, [key]: !settings[key] };
    setSettings(newSettings);
    setIsSaving(true);
    setError(null);
    setSaveSuccess(false);

    try {
      await updateSettings('features', newSettings);
      setSaveSuccess(true);
      setTimeout(() => setSaveSuccess(false), UI_CONSTANTS.NOTIFICATION_DELAY_MS);
    } catch {
      // Revert on error
      setSettings(settings);
      setError('Error al guardar');
    } finally {
      setIsSaving(false);
    }
  };

  const getFeaturesByCategory = (category: string) =>
    featureConfigs.filter((f) => f.category === category);

  const getCategoryLabel = (category: string) => {
    switch (category) {
      case 'core':
        return 'Funciones Principales';
      case 'ai':
        return 'Inteligencia Artificial';
      case 'marketing':
        return 'Marketing y Engagement';
      default:
        return category;
    }
  };

  if (isLoading) {
    return (
      <div className="bg-white rounded-lg shadow p-6">
        <div className="animate-pulse space-y-4">
          <div className="h-8 bg-gray-200 rounded w-1/4"></div>
          <div className="h-32 bg-gray-200 rounded"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="bg-white rounded-lg shadow p-6">
        <h2 className="text-xl font-bold text-gray-900 mb-2">Feature Flags</h2>
        <p className="text-gray-600 mb-6">
          Activa o desactiva funcionalidades de la tienda sin necesidad de hacer deploy.
        </p>

        {(error ?? apiError) && (
          <div className="mb-6 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg">
            {error ?? apiError}
          </div>
        )}

        {saveSuccess && (
          <div className="mb-6 bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded-lg">
            Configuracion guardada
          </div>
        )}

        <div className="space-y-8">
          {['ai', 'core', 'marketing'].map((category) => (
            <div key={category}>
              <h3 className="text-lg font-medium text-gray-900 mb-4 flex items-center gap-2">
                <CategoryIcon category={category} />
                {getCategoryLabel(category)}
              </h3>
              <div className="space-y-4">
                {getFeaturesByCategory(category).map((feature) => (
                  <div
                    key={feature.key}
                    className="flex items-center justify-between p-4 bg-gray-50 rounded-lg"
                  >
                    <div className="flex-1">
                      <p className="font-medium text-gray-900">{feature.label}</p>
                      <p className="text-sm text-gray-500">{feature.description}</p>
                    </div>
                    <Toggle
                      enabled={settings[feature.key]}
                      onChange={() => handleToggle(feature.key)}
                      disabled={isSaving}
                    />
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Status Summary */}
      <div className="bg-white rounded-lg shadow p-6">
        <h3 className="text-lg font-medium text-gray-900 mb-4">Resumen</h3>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <StatCard
            label="Activas"
            value={Object.values(settings).filter(Boolean).length}
            total={featureConfigs.length}
            color="green"
          />
          <StatCard
            label="IA"
            value={getFeaturesByCategory('ai').filter((f) => settings[f.key]).length}
            total={getFeaturesByCategory('ai').length}
            color="purple"
          />
          <StatCard
            label="Core"
            value={getFeaturesByCategory('core').filter((f) => settings[f.key]).length}
            total={getFeaturesByCategory('core').length}
            color="blue"
          />
          <StatCard
            label="Marketing"
            value={getFeaturesByCategory('marketing').filter((f) => settings[f.key]).length}
            total={getFeaturesByCategory('marketing').length}
            color="orange"
          />
        </div>
      </div>
    </div>
  );
}

function Toggle({
  enabled,
  onChange,
  disabled,
}: {
  enabled: boolean;
  onChange: () => void;
  disabled?: boolean;
}) {
  return (
    <button
      type="button"
      onClick={onChange}
      disabled={disabled}
      className={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-orange-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed ${
        enabled ? 'bg-orange-500' : 'bg-gray-200'
      }`}
      role="switch"
      aria-checked={enabled}
    >
      <span
        className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${
          enabled ? 'translate-x-5' : 'translate-x-0'
        }`}
      />
    </button>
  );
}

function StatCard({
  label,
  value,
  total,
  color,
}: {
  label: string;
  value: number;
  total: number;
  color: 'green' | 'purple' | 'blue' | 'orange';
}) {
  const colorClasses = {
    green: 'bg-green-50 text-green-700',
    purple: 'bg-purple-50 text-purple-700',
    blue: 'bg-blue-50 text-blue-700',
    orange: 'bg-orange-50 text-orange-700',
  };

  return (
    <div className={`rounded-lg p-4 ${colorClasses[color]}`}>
      <p className="text-sm font-medium">{label}</p>
      <p className="text-2xl font-bold">
        {value}/{total}
      </p>
    </div>
  );
}

function CategoryIcon({ category }: { category: string }) {
  const className = 'w-5 h-5';
  switch (category) {
    case 'ai':
      return (
        <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
        </svg>
      );
    case 'core':
      return (
        <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
        </svg>
      );
    case 'marketing':
      return (
        <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 3.055A9.001 9.001 0 1020.945 13H11V3.055z" />
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20.488 9H15V3.512A9.025 9.025 0 0120.488 9z" />
        </svg>
      );
    default:
      return null;
  }
}
